import React, { useState, useEffect } from 'react';
import LoadingSpinner from '../components/common/LoadingSpinner';
import PublicFooter from '../components/layout/PublicFooter';
import { Link, useNavigate } from 'react-router-dom';
import {
  Search,
  Brain,
  RefreshCw,
  Shield,
  SlidersHorizontal,
  ChevronDown,
  ArrowRight,
  X,
  Check,
  BadgeCheck,
  BarChart2,
  Code2,
  Rocket,
} from 'lucide-react';
import PublicTopNav from '../components/layout/PublicTopNav';
import HeroDashboardMock from '../components/landing/HeroDashboardMock';
import ScreenshotFrame from '../components/landing/ScreenshotFrame';
import StrategySparkline from '../components/landing/StrategySparkline';
import TrustBand from '../components/landing/TrustBand';
import marketplaceService from '../services/marketplaceService';
import { MarketplaceListing, BacktestResponse } from '../types/marketplace';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [listings, setListings] = useState<MarketplaceListing[]>([]);
  const [listingsLoading, setListingsLoading] = useState(true);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  // Per-card reveal flag — regulation compliance: backtest numbers AND the
  // equity-curve sparkline are not shown by default. The user must click
  // through to reveal them, which also lazily fetches the backtest series.
  const [revealedBacktests, setRevealedBacktests] = useState<Set<string>>(new Set());
  // Lazily-fetched backtest series, keyed by basket_id. `null` = fetched but
  // none available. Drives the sparkline + trades count once a card is revealed.
  const [backtests, setBacktests] = useState<Record<string, BacktestResponse | null>>({});
  const [loadingBacktests, setLoadingBacktests] = useState<Set<string>>(new Set());

  const toggleBacktest = (basketId: string) => {
    setRevealedBacktests((prev) => {
      const next = new Set(prev);
      if (next.has(basketId)) {
        next.delete(basketId);
        return next;
      }
      next.add(basketId);
      // First reveal — lazily fetch the backtest series (public, 5-min cached).
      if (!(basketId in backtests) && !loadingBacktests.has(basketId)) {
        setLoadingBacktests((s) => new Set(s).add(basketId));
        marketplaceService
          .getBacktest(basketId)
          .then((data) => setBacktests((m) => ({ ...m, [basketId]: data })))
          .catch(() => setBacktests((m) => ({ ...m, [basketId]: null })))
          .finally(() =>
            setLoadingBacktests((s) => {
              const n = new Set(s);
              n.delete(basketId);
              return n;
            }),
          );
      }
      return next;
    });
  };

  useEffect(() => {
    const fetchListings = async () => {
      try {
        const data = await marketplaceService.getListings();
        setListings(data.slice(0, 4));
      } catch {
        setListings([]);
      } finally {
        setListingsLoading(false);
      }
    };
    fetchListings();
  }, []);

  const heroBadges = [
    { icon: BadgeCheck, label: 'Curated & Verified', sub: 'High quality strategies' },
    { icon: Rocket, label: 'One Click Deploy', sub: 'Live in minutes' },
    { icon: BarChart2, label: 'Transparent Performance', sub: 'Backtested & live stats' },
  ];


  const features = [
    {
      icon: Brain,
      title: 'Exchange Approved Strategies',
      desc: 'Access exchange-approved algo strategies with transparent backtest records.',
    },
    {
      icon: RefreshCw,
      title: 'Always Up-to-Date',
      desc: 'Basket updates from publishers sync automatically to your account.',
    },
    {
      icon: Shield,
      title: 'Full Control',
      desc: 'Pause, resume, or cancel any subscription at any time. Your account, your rules.',
    },
    {
      icon: SlidersHorizontal,
      title: 'Flexible Sizing',
      desc: 'Adjust lot multipliers to match your capital and risk appetite.',
    },
  ];

  const faqs = [
    {
      q: 'How does the marketplace work?',
      a: 'Exchange-approved algo baskets are published on the marketplace. Each basket contains one or more strategies that execute trades automatically in your trading account.',
    },
    {
      q: 'Do I need coding skills?',
      a: 'No. The marketplace is designed for traders who want algo execution without writing code. Simply subscribe and configure your lot size.',
    },
    {
      q: 'What happens when a publisher updates a basket?',
      a: 'Updates sync automatically to all subscribers. You always trade with the latest version of the basket.',
    },
    {
      q: 'Can I control my risk?',
      a: 'Yes. You set your own lot multiplier when subscribing. You can also pause or cancel any subscription instantly.',
    },
    {
      q: 'How do I connect my trading account?',
      a: 'Log in with your trading account credentials to link your account. Once connected, subscribed strategies execute trades automatically.',
    },
  ];

  const comparison = [
    { manual: 'Emotional buy/sell decisions', algo: 'Rule-based, emotion-free execution' },
    { manual: 'Limited to screen time', algo: 'Monitors markets all day' },
    { manual: 'Slow manual order placement', algo: 'Millisecond order execution' },
    { manual: 'No historical validation', algo: '5-year backtested strategies' },
    { manual: 'Hard to scale position size', algo: 'Set lot size and scale instantly' },
  ];

  const riskColor = (level: string) => {
    const l = level.toLowerCase();
    if (l === 'low') return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300';
    if (l === 'medium') return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300';
    return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300';
  };

  // Builder CTA target: signed-in visitors go straight to the basket builder,
  // everyone else to the auth screen.
  const isAuthed = (() => {
    try {
      return !!localStorage.getItem('auth_tokens');
    } catch {
      return false;
    }
  })();
  const builderCtaTarget = isAuthed ? '/baskets' : '/build-your-own';

  const builderPoints = [
    'Multi-leg option strategies — CE/PE, buy/sell, and per-leg lot sizing',
    'Strike selection your way — by premium, ATM, or straddle',
    'Built-in stop-loss, target, trailing SL, and re-entry rules',
    'Schedule entries and exits by time and weekday — fully automated',
  ];

  const masked = '•••';

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950 text-gray-900 dark:text-white">
      <PublicTopNav />

      {/* Hero — light, two-column (copy + CTAs on the left, dashboard mock on the right) */}
      <section className="relative overflow-hidden bg-gradient-to-b from-zebu-50/70 via-white to-white dark:from-gray-900 dark:via-gray-950 dark:to-gray-950">
        <div className="absolute top-0 right-0 w-[28rem] h-[28rem] bg-zebu-200/30 dark:bg-zebu-900/20 rounded-full blur-3xl -z-0" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left */}
            <div>
              <h1 className="text-4xl sm:text-5xl font-black tracking-tight leading-[1.1] text-gray-900 dark:text-white">
                Invest Smarter,
                <br />
                With Algo Strategies
              </h1>
              <p className="mt-5 text-lg text-gray-600 dark:text-gray-300 max-w-lg">
                Discover, deploy, and automate algorithmic strategies crafted by experts.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/marketplace"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-zebu-600 hover:bg-zebu-700 text-white font-semibold shadow-lg shadow-zebu-600/20 hover:shadow-xl transition-all"
                >
                  Explore Strategies <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to={builderCtaTarget}
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl border border-zebu-200 dark:border-zebu-800 text-zebu-700 dark:text-zebu-300 font-semibold hover:bg-zebu-50 dark:hover:bg-zebu-900/30 transition-all"
                >
                  Build Your Own Algo <Code2 className="h-4 w-4" />
                </Link>
              </div>

              {/* Trust chips */}
              <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
                {heroBadges.map((b) => (
                  <div key={b.label} className="flex items-start gap-2.5">
                    <b.icon className="h-5 w-5 text-zebu-600 dark:text-zebu-400 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-gray-900 dark:text-white leading-tight">{b.label}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{b.sub}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right — dashboard mock */}
            <div className="relative">
              <HeroDashboardMock />
            </div>
          </div>
        </div>
      </section>

      {/* Top Strategies marketplace */}
      <section className="bg-white dark:bg-gray-950 py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-2">
            <div className="flex items-center gap-3">
              <h2 className="text-2xl font-bold">Top Strategies</h2>
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-zebu-50 dark:bg-zebu-900/40 text-zebu-600 dark:text-zebu-300">
                Marketplace
              </span>
            </div>
            <Link
              to="/marketplace"
              className="hidden sm:inline-flex items-center gap-1 text-sm font-medium text-zebu-600 hover:text-zebu-700 dark:text-zebu-400 dark:hover:text-zebu-300 transition-colors"
            >
              View All Strategies <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-8">
            Explore proven algorithmic strategies with consistent performance.
          </p>

          {listingsLoading ? (
            <LoadingSpinner message="Loading strategies..." />
          ) : listings.length === 0 ? (
            <div className="text-center py-12">
              <Search className="h-8 w-8 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500 dark:text-gray-400">No strategies available yet. Check back soon.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 items-stretch">
              {listings.map((listing) => {
                const revealed = revealedBacktests.has(listing.basket_id);
                const hasBacktest = !!listing.backtest_uploaded_at;
                const bt = backtests[listing.basket_id];
                const isLoading = loadingBacktests.has(listing.basket_id);
                // All values come from the fetched backtest stats (the same
                // source as the marketplace detail page), shown once the card is
                // revealed and the fetch has settled.
                const cagr = bt?.stats?.cagr;
                const totalReturn = bt?.stats?.totalReturn;
                const winRate = bt?.stats?.winRate;
                const statsReady = revealed && !isLoading;
                const trend = (v?: number) =>
                  v === undefined
                    ? 'text-gray-900 dark:text-white'
                    : v >= 0
                    ? 'text-green-600 dark:text-green-400'
                    : 'text-red-600 dark:text-red-400';

                const StatValue: React.FC<{ children: React.ReactNode }> = ({ children }) =>
                  statsReady ? (
                    <>{children}</>
                  ) : (
                    <span className="text-gray-300 dark:text-gray-600 select-none">{masked}</span>
                  );

                return (
                  <Link
                    key={listing.basket_id}
                    to={`/marketplace/${listing.basket_id}`}
                    className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden hover:border-zebu-400 dark:hover:border-zebu-600 hover:shadow-lg transition-all group flex flex-col h-full"
                  >
                    <div className="p-5 flex flex-col flex-1">
                      {/* Header — monogram + title + risk */}
                      <div className="flex items-start gap-3 mb-3">
                        <div className="h-10 w-10 rounded-lg bg-zebu-50 dark:bg-zebu-900/40 flex items-center justify-center shrink-0 text-zebu-600 dark:text-zebu-300 font-bold">
                          {listing.title.charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="font-semibold text-gray-900 dark:text-white group-hover:text-zebu-600 dark:group-hover:text-zebu-400 transition-colors leading-snug line-clamp-2">
                            {listing.title}
                          </h3>
                          <span className="inline-block mt-1 text-[0.65rem] font-medium px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">
                            {listing.category}
                          </span>
                        </div>
                        <span className={`shrink-0 text-[0.65rem] font-medium px-2 py-0.5 rounded-full ${riskColor(listing.risk_level)}`}>
                          {listing.risk_level}
                        </span>
                      </div>

                      {/* Stats row — masked until revealed (compliance) */}
                      <div className="grid grid-cols-3 gap-2 mb-3">
                        <div>
                          <p className="text-[0.6rem] text-gray-400 dark:text-gray-500 uppercase whitespace-nowrap">CAGR</p>
                          <p className={`text-sm font-bold tabular-nums ${trend(cagr)}`}>
                            <StatValue>
                              {cagr !== undefined ? `${cagr >= 0 ? '+' : ''}${cagr}%` : '—'}
                            </StatValue>
                          </p>
                        </div>
                        <div>
                          <p className="text-[0.6rem] text-gray-400 dark:text-gray-500 uppercase whitespace-nowrap">Return%</p>
                          <p className={`text-sm font-bold tabular-nums ${trend(totalReturn)}`}>
                            <StatValue>
                              {totalReturn !== undefined ? `${totalReturn >= 0 ? '+' : ''}${totalReturn}%` : '—'}
                            </StatValue>
                          </p>
                        </div>
                        <div>
                          <p className="text-[0.6rem] text-gray-400 dark:text-gray-500 uppercase whitespace-nowrap">Win Rate</p>
                          <p className="text-sm font-bold tabular-nums text-gray-900 dark:text-white">
                            <StatValue>
                              {winRate !== undefined ? `${winRate}%` : '—'}
                            </StatValue>
                          </p>
                        </div>
                      </div>

                      {/* Sparkline slot — fixed height keeps every card aligned.
                          Shown only after reveal + successful fetch (compliance). */}
                      <div className="h-14 mb-4">
                        {revealed ? (
                          isLoading ? (
                            <div className="h-full flex items-center justify-center">
                              <RefreshCw className="h-4 w-4 animate-spin text-gray-300 dark:text-gray-600" />
                            </div>
                          ) : bt?.stats?.dailyEquity?.length ? (
                            <StrategySparkline data={bt.stats.dailyEquity} height={56} />
                          ) : (
                            <div className="h-full flex items-center justify-center text-[0.65rem] text-gray-400 dark:text-gray-500">
                              No chart available
                            </div>
                          )
                        ) : (
                          <div className="h-full rounded-md bg-gradient-to-b from-gray-50 to-transparent dark:from-gray-800/40 flex items-center justify-center text-[0.65rem] text-gray-400 dark:text-gray-500">
                            Chart hidden
                          </div>
                        )}
                      </div>

                      {/* Footer — reveal toggle + Subscribe (pinned to bottom).
                          Subscribe sends logged-out visitors to the login page;
                          signed-in users go to the basket detail to subscribe. */}
                      <div className="mt-auto space-y-2">
                        {hasBacktest ? (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              toggleBacktest(listing.basket_id);
                            }}
                            className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg border border-zebu-200 dark:border-zebu-800 text-sm font-medium text-zebu-600 dark:text-zebu-400 hover:bg-zebu-50 dark:hover:bg-zebu-900/30 transition-colors"
                            aria-label={revealed ? 'Hide performance' : 'Show performance'}
                          >
                            <BarChart2 className="h-4 w-4" />
                            {revealed ? 'Hide Performance' : 'Show Performance'}
                          </button>
                        ) : (
                          <span className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-800 text-sm font-medium text-gray-500 dark:text-gray-400 group-hover:text-zebu-600 dark:group-hover:text-zebu-400 transition-colors">
                            View Details <ArrowRight className="h-3.5 w-3.5" />
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            navigate(isAuthed ? `/marketplace/${listing.basket_id}` : '/auth');
                          }}
                          className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg bg-zebu-600 hover:bg-zebu-700 text-sm font-semibold text-white transition-colors"
                        >
                          Subscribe <ArrowRight className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}

          <div className="text-center mt-10 sm:hidden">
            <Link
              to="/marketplace"
              className="inline-flex items-center gap-1 text-sm font-medium text-zebu-600 hover:text-zebu-700 dark:text-zebu-400 dark:hover:text-zebu-300 transition-colors"
            >
              View All Strategies <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Build your own — white-box strategy builder */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-zebu-50 dark:bg-zebu-900/30 text-zebu-700 dark:text-zebu-300 text-xs font-semibold uppercase tracking-wider">
                <SlidersHorizontal className="h-3.5 w-3.5" />
                Build your own
              </span>
              <h2 className="text-2xl font-bold mt-4">Prefer to build your own strategies?</h2>
              <p className="mt-3 text-gray-600 dark:text-gray-400">
                You&rsquo;re not limited to subscribing. Use the no-code strategy builder to create
                your own baskets and multi-leg option strategies — configured exactly how you trade,
                then automated end-to-end.
              </p>
              <Link
                to={builderCtaTarget}
                className="mt-6 inline-flex items-center gap-1.5 px-6 py-3 rounded-xl bg-zebu-600 hover:bg-zebu-700 text-white font-semibold shadow-lg hover:shadow-xl transition-all"
              >
                Start Building <ArrowRight className="h-4 w-4" />
              </Link>
              <ul className="mt-8 space-y-3">
                {builderPoints.map((point) => (
                  <li
                    key={point}
                    className="flex items-start gap-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3"
                  >
                    <Check className="h-5 w-5 text-zebu-600 dark:text-zebu-400 shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{point}</span>
                  </li>
                ))}
              </ul>
            </div>
            <ScreenshotFrame
              srcDark="/screenshots/strategy-builder-dark.png"
              srcLight="/screenshots/strategy-builder-light.png"
              alt="Zebu no-code strategy builder"
              caption="zebu · strategy builder"
              disclaimer="Disclaimer: Sample strategy configuration shown for demonstration purposes only and should not be construed as investment advice or a recommendation to trade."
            />
          </div>
        </div>
      </section>

      {/* Manual vs Algo comparison */}
      <section id="how-it-works" className="bg-gray-50 dark:bg-gray-900 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-center mb-3">Why Algo Trading?</h2>
          <p className="text-center text-gray-500 dark:text-gray-400 text-sm mb-10 max-w-xl mx-auto">
            Stop letting emotions and screen fatigue cost you money.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Manual */}
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-800">
              <div className="bg-gray-100 dark:bg-gray-800 px-5 py-3">
                <p className="font-semibold text-gray-500 dark:text-gray-400 text-sm uppercase tracking-wider">Manual Trading</p>
              </div>
              <div className="divide-y divide-gray-100 dark:divide-gray-800">
                {comparison.map((row, i) => (
                  <div key={i} className="flex items-center gap-3 px-5 py-3">
                    <X className="h-4 w-4 text-red-400 shrink-0" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">{row.manual}</span>
                  </div>
                ))}
              </div>
            </div>
            {/* Algo */}
            <div className="rounded-xl border border-zebu-200 dark:border-zebu-800 overflow-hidden bg-white dark:bg-gray-800 shadow-sm shadow-zebu-100 dark:shadow-none">
              <div className="bg-zebu-50 dark:bg-zebu-900/30 px-5 py-3">
                <p className="font-semibold text-zebu-700 dark:text-zebu-300 text-sm uppercase tracking-wider">Algo Trading (Zebu Marketplace)</p>
              </div>
              <div className="divide-y divide-zebu-50 dark:divide-zebu-900/20">
                {comparison.map((row, i) => (
                  <div key={i} className="flex items-center gap-3 px-5 py-3">
                    <Check className="h-4 w-4 text-zebu-500 shrink-0" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{row.algo}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-center mb-12">Why Use the Marketplace</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((f) => (
              <div
                key={f.title}
                className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-5"
              >
                <f.icon className="h-6 w-6 text-zebu-600 mb-3" />
                <h3 className="font-semibold mb-1">{f.title}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-gray-50 dark:bg-gray-900 py-16">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-center mb-12">Frequently Asked Questions</h2>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div
                key={i}
                className="border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden bg-white dark:bg-gray-800"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors"
                >
                  {faq.q}
                  <ChevronDown
                    className={`h-4 w-4 text-gray-400 transition-transform ${
                      openFaq === i ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-4 text-sm text-gray-600 dark:text-gray-400">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom reassurance band */}
      <TrustBand />

      <PublicFooter />
    </div>
  );
};

export default LandingPage;
